/**
 * Maps the Auth Letter Upload modal's authorization tabs onto the
 * `coverage/candid/authorizations` request body.
 *
 * @module buildCandidAuthorizationsRequest
 */
import moment from "moment";
import {
  CandidAuthorizationEntry,
  CandidAuthorizationsRequest,
} from "../../../api/services/candid/candidApiTypes";
import { AuthLetterUploadFormState } from "../../../data-model/candidAuthorizationModel";

/** Date format Candid expects for the authorization period. */
const API_DATE_FORMAT = "YYYY-MM-DD";

/** Date formats the modal's pickers can produce, in match priority order. */
const FORM_DATE_FORMATS = ["MM/DD/YYYY", "YYYY-MM-DD", moment.ISO_8601];

/** Order-level context the authorization tabs do not carry. */
export interface CandidAuthorizationsContext {
  /** PA order identifier. */
  orderId: string;
  /** Organisation identifier. */
  orgId: string;
  /** Patient MRN from `generalOrderDetails.demographics`. */
  mrn: string;
}

/**
 * Converts a form date into the `YYYY-MM-DD` form Candid expects.
 *
 * @param value - Date string as entered on the form
 * @returns The normalised date, or an empty string when unparseable
 */
const toApiDate = (value: string): string => {
  const parsed = moment(value, FORM_DATE_FORMATS, false);
  return parsed.isValid() ? parsed.format(API_DATE_FORMAT) : "";
};

/**
 * Maps one authorization tab onto a Candid authorization entry.
 *
 * @param authorization - A single authorization tab from the modal
 * @returns The matching request entry
 */
const toAuthorizationEntry = (
  authorization: AuthLetterUploadFormState,
): CandidAuthorizationEntry => ({
  payer_id: authorization.payerId,
  payer_name: authorization.payerName,
  authorization_number: authorization.authorizationNumber,
  cpt_codes: authorization.serviceCodes,
  units: authorization.units.toUpperCase(),
  quantity: Number(authorization.quantity) || 0,
  period_start: toApiDate(authorization.startDate),
  period_end: toApiDate(authorization.endDate),
  dx_codes: authorization.dx_codes,
  notes: authorization.notes,
  billing_provider_npi: authorization.npiBillingNumber,
});

/**
 * Builds the `coverage/candid/authorizations` request body from the modal's tabs.
 *
 * MRN is order-level and comes from `generalOrderDetails`, not the form.
 *
 * @param authorizations - One entry per sidebar tab in the modal
 * @param context - Order, organisation, and MRN identifiers the tabs do not carry
 * @returns Request body for `CandidService.postAuthorizations`
 */
export const buildCandidAuthorizationsRequest = (
  authorizations: AuthLetterUploadFormState[],
  context: CandidAuthorizationsContext,
): CandidAuthorizationsRequest => ({
  order_id: context.orderId,
  org_id: context.orgId,
  mrn: context.mrn,
  authorizations: authorizations.map(toAuthorizationEntry),
});
